import * as angular from "angular";

export class CredentialsManager {
    public campaignid: any = null;
    public uid: string | number | null | undefined;
    public oid: any = null;
    public iframePromise: any;
    public leaderView = false;

    private readonly $cookies: any;
    private readonly $window: any;
    private readonly $q: any;
    private readonly __env: any;
    private readonly $location: any;

    constructor(
        $cookies: any,
        $window: any,
        $q: any,
        __env: any,
        $location: any
    ) {
        this.$cookies = $cookies;
        this.$window = $window;
        this.$q = $q;
        this.__env = __env;
        this.$location = $location;
    }

    setCredentials(
        campaignid: string | number,
        access_token: any,
        oid?: string | number,
        leaderView: boolean = false,
        uid?: string | number
    ): void {
        this.campaignid = campaignid;
        this.uid = uid;
        this.oid = oid;
        this.leaderView = leaderView;
        this.setAccessToken(access_token);
    }

    setAccessToken(access_token: any): void {
        let now = new Date();
        let exp = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + 1,
            now.getHours(),
            now.getMinutes(),
            now.getSeconds()
        );
        let domain =
            this.__env.host != "localhost"
                ? "." + this.__env.host
                : this.__env.host;

        this.$cookies.put("access_token", access_token, {
            expires: exp,
            domain: domain,
            path: "/",
        });
    }

    getAccessToken(): any {
        return this.$cookies.get("access_token");
    }

    removeAccessToken(): void {
        this.$cookies.remove("access_token");
    }

    redirectURI(region?: string): string {
        return region
            ? "https://" + region + "." + this.__env.host + "/auth.html"
            : this.$window.location.origin + "/auth.html";
    }

    reauth(campaignid?: string | number, oid?: string | number): Promise<any> {
        if (angular.isUndefined(campaignid)) campaignid = this.campaignid; //when accesstoken has expired, not changing campaign.
        if (angular.isDefined(oid) && oid != null) {
            this.oid = oid;
        }
        if (this.iframePromise) {
            return this.iframePromise;
        }

        const defer = this.$q.defer();
        this.iframePromise = defer.promise;
        let ifra: any = document.createElement("iframe");
        let redirect_uri = this.redirectURI();
        let full_url =
            window.location.origin +
            "/api/oauth2/authorize/" +
            campaignid +
            "/token/ecanvasser/relogin?redirect_uri=" +
            redirect_uri;
        ifra.src = full_url;
        ifra.setAttribute("height", "0");
        ifra.setAttribute("width", "0");
        ifra.style.display = "none";
        ifra.onload = () => {
            if (!this.receivedAccessToken(ifra)) {
                // authorize sends an expired or refused session to logout.php,
                // so retrying with the old token can only fail again.
                this.$window.location.href =
                    "/logout.php?r=" + this.$location.url();
            } else {
                this.campaignid = campaignid;
                defer.resolve();
                //remove the iframe
                ifra.outerHTML = "";
                ifra = null;
                this.iframePromise = null;
            }
        };
        void document.body.appendChild(ifra);
        return this.iframePromise;
    }

    private receivedAccessToken(ifra: HTMLIFrameElement): boolean {
        try {
            const location = ifra.contentWindow?.location;
            return (
                location?.pathname === "/auth.html" &&
                location.hash.indexOf("access_token=") !== -1
            );
        } catch {
            // A cross-origin landing page can't be read, so it isn't auth.html.
            return false;
        }
    }

    // The other region's dashboard is a region-suffixed sibling of the
    // current host: appeu <-> appusa, betaeu <-> betausa - so keeping the
    // deployment prefix is what keeps beta traffic on beta. Anything else
    // (a white-label subdomain, localhost, single-region develop) falls
    // back to the app{region} hosts, which is where those flows already
    // went.
    crossRegionHost(region: string): string {
        const label = String(
            this.$window.location.hostname.split(".")[0] || ""
        );
        const match = label.match(/^(app|beta)(eu|usa|ap)?$/);
        const prefix = match ? match[1] : "app";
        return "https://" + prefix + region + "." + this.__env.host;
    }

    // Carries the session to the other region's server: its /login/handoff
    // endpoint trades the single-use token for a signed-in session there,
    // then lands on `destination` via auth.html. Our own session and cookies
    // are already gone - the API destroyed them when it minted the token.
    handoff(region: string, token: string, destination: string): void {
        this.removeAccessToken();
        this.$window.location =
            this.crossRegionHost(region) +
            "/api/login/handoff/" +
            token +
            "?destination=" +
            encodeURIComponent(destination);
    }

    redirect(
        state: string,
        campaignid: string | number,
        region?: string,
        signin_redirect?: string
    ): void {
        console.log(signin_redirect);
        this.removeAccessToken();
        const host = this.__env.host;
        const redirect_uri = this.redirectURI(region);
        this.$window.location =
            (angular.isDefined(region) && region
                ? "https://" + region + "." + host
                : "") +
            "/api/oauth2/authorize/" +
            campaignid +
            "/token/ecanvasser/" +
            state +
            "?redirect_uri=" +
            redirect_uri +
            "&signin_redirect=" +
            encodeURIComponent(signin_redirect as any);
    }
}

angular
    .module("credentialsManager", [])
    .service("CredentialsManager", CredentialsManager);
